import { useReactFlow } from '@xyflow/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { WaitNodeData } from '../../../converters/shared/reactFlowTypes'

/**
 * Hook for Wait node functionality.
 * Handles duration (number) and ringDuringWait (boolean) updates.
 */
export const useWaitNode = (id: string, data: WaitNodeData) => {
  const { updateNodeData } = useReactFlow()
  const dataRef = useRef(data)
  const isUpdatingRef = useRef(false)

  // Keep dataRef in sync with data
  useEffect(() => {
    dataRef.current = data
  }, [data])

  const [duration, setDuration] = useState(
    data.duration !== undefined ? String(data.duration) : '',
  )
  const ringDuringWait = data.ringDuringWait === true

  // Sync local state when node data changes from outside (not from our own updates)
  useEffect(() => {
    if (!isUpdatingRef.current) {
      const newDuration =
        data.duration !== undefined ? String(data.duration) : ''
      setDuration((prev) => (newDuration !== prev ? newDuration : prev))
    }
  }, [data.duration])

  const handleDurationChange = useCallback((newDuration: string) => {
    // Only update local state - don't update React Flow yet
    isUpdatingRef.current = true
    setDuration(newDuration)
  }, [])

  const handleDurationIncrement = useCallback(() => {
    const current = duration ? parseInt(duration, 10) : 0
    if (!isNaN(current)) {
      const newValue = Math.min(60, current + 1)
      setDuration(String(newValue))
      isUpdatingRef.current = true
      updateNodeData(id, {
        ...dataRef.current,
        duration: newValue,
      })
      isUpdatingRef.current = false
    }
  }, [duration, id, updateNodeData])

  const handleDurationDecrement = useCallback(() => {
    const current = duration ? parseInt(duration, 10) : 0
    if (!isNaN(current) && current > 0) {
      const newValue = Math.max(1, current - 1)
      setDuration(String(newValue))
      isUpdatingRef.current = true
      updateNodeData(id, {
        ...dataRef.current,
        duration: newValue,
      })
      isUpdatingRef.current = false
    }
  }, [duration, id, updateNodeData])

  const handleDurationBlur = useCallback(() => {
    // Update React Flow when user is done typing (on blur)
    const numValue = duration ? parseInt(duration, 10) : undefined
    // Clamp to valid range: 1-60
    const clampedValue =
      numValue && !isNaN(numValue)
        ? Math.max(1, Math.min(60, numValue))
        : undefined
    updateNodeData(id, {
      ...dataRef.current,
      duration: clampedValue || undefined,
    })
    isUpdatingRef.current = false
  }, [id, updateNodeData, duration])

  const handleRingDuringWaitChange = useCallback(
    (ringDuringWait: boolean) => {
      updateNodeData(id, {
        ...dataRef.current,
        ringDuringWait,
      })
    },
    [id, updateNodeData],
  )

  return {
    currentDuration: duration,
    currentRingDuringWait: ringDuringWait,
    handleDurationChange,
    handleDurationBlur,
    handleDurationIncrement,
    handleDurationDecrement,
    handleRingDuringWaitChange,
    hasData: !!duration || ringDuringWait,
  }
}
